// src/pages/FormBuilder/formsUtils.js
//
// Round-trip mapping between the visual builder's local `formData` shape and the
// backend Form bundle (form/render <-> form/save). The builder keeps a rich,
// nested UI shape (branding / submit / esign / fields); the backend stores flat
// Theme* columns + nested *_list collections. These helpers translate both ways
// so a form can be loaded, edited and saved losslessly.
//
// Lossless branding/submit: the full `branding` and `submit` objects are stashed
// in `ThemeJson` (which is a frontend-owned blob today) AND mirrored into the
// individual Theme* columns for any server-side rendering. On load we prefer
// ThemeJson and fall back to reconstructing branding from the columns.

import {
  initialFormBuilderData,
  DEFAULT_ESIGN_CONFIRMATION,
  FORM_FIELD_TYPES,
} from "./formBuilderMockData";

let localKeySeed = 0;
const nextLocalId = (prefix = "field") => {
  localKeySeed += 1;
  return `${prefix}_${localKeySeed}_${Math.random().toString(16).slice(2, 8)}`;
};

const safeParseJson = (value, fallback = null) => {
  if (value === null || value === undefined || value === "") return fallback;
  if (typeof value === "object") return value;
  try {
    return JSON.parse(value);
  } catch (e) {
    return fallback;
  }
};

const toNumberOrNull = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(value);
  return Number.isNaN(n) ? null : n;
};

// ---------------------------------------------------------------------------
// Response unwrapping
// ---------------------------------------------------------------------------

// Pull the form bundle out of an API response, tolerating a couple of shapes.
export const extractFormBundle = (response) => {
  if (!response) return null;
  return (
    response.form_data ||
    response.data?.form_data ||
    (response.Id ? response : null)
  );
};

// Dig the new/updated Form_Id out of a save/duplicate response.
export const extractSavedFormId = (response) => {
  return (
    response?.Form_Id ||
    response?.form_data?.Id ||
    response?.data?.Form_Id ||
    response?.data?.form_data?.Id ||
    null
  );
};

// ---------------------------------------------------------------------------
// Branding <-> Theme columns
// ---------------------------------------------------------------------------

const themeColumnsFromBranding = (branding = {}) => ({
  PublicTitle: branding.title ?? null,
  PublicDescription: branding.description ?? null,
  LogoUrl: branding.logoUrl ?? null,
  SubmitButtonText: branding.buttonText ?? "Submit",
  ThemePrimaryColor: branding.primaryColor ?? null,
  ThemeAccentColor: branding.accentColor ?? null,
  ThemeBackgroundColor: branding.backgroundColor ?? null,
  ThemeCardColor: branding.cardColor ?? null,
  ThemeTitleColor: branding.titleColor ?? null,
  ThemeDescriptionColor: branding.descriptionColor ?? null,
  ThemeTextColor: branding.textColor ?? null,
  ThemeLabelColor: branding.labelColor ?? null,
  ThemeRequiredColor: branding.requiredColor ?? null,
  ThemeInputBackgroundColor: branding.inputBackgroundColor ?? null,
  ThemeInputTextColor: branding.inputTextColor ?? null,
  ThemeInputBorderColor: branding.inputBorderColor ?? null,
  ThemeInputFocusColor: branding.inputFocusColor ?? null,
  ThemePlaceholderColor: branding.placeholderColor ?? null,
  ThemeBorderRadius: toNumberOrNull(branding.borderRadius),
  ThemeInputRadius: toNumberOrNull(branding.inputRadius),
  ThemeFieldSpacing: toNumberOrNull(branding.fieldSpacing),
  ThemeInputHeight: toNumberOrNull(branding.inputHeight),
});

// Reconstruct a branding object from the flat Theme* columns (fallback path when
// ThemeJson is absent, e.g. a form seeded outside the builder).
const brandingFromColumns = (form = {}) => ({
  logoUrl: form.LogoUrl || "",
  title: form.PublicTitle || "",
  description: form.PublicDescription || "",
  primaryColor: form.ThemePrimaryColor || undefined,
  accentColor: form.ThemeAccentColor || undefined,
  backgroundColor: form.ThemeBackgroundColor || undefined,
  cardColor: form.ThemeCardColor || undefined,
  titleColor: form.ThemeTitleColor || undefined,
  descriptionColor: form.ThemeDescriptionColor || undefined,
  textColor: form.ThemeTextColor || undefined,
  labelColor: form.ThemeLabelColor || undefined,
  requiredColor: form.ThemeRequiredColor || undefined,
  inputBackgroundColor: form.ThemeInputBackgroundColor || undefined,
  inputTextColor: form.ThemeInputTextColor || undefined,
  inputBorderColor: form.ThemeInputBorderColor || undefined,
  inputFocusColor: form.ThemeInputFocusColor || undefined,
  placeholderColor: form.ThemePlaceholderColor || undefined,
  borderRadius: toNumberOrNull(form.ThemeBorderRadius) ?? undefined,
  inputRadius: toNumberOrNull(form.ThemeInputRadius) ?? undefined,
  fieldSpacing: toNumberOrNull(form.ThemeFieldSpacing) ?? undefined,
  inputHeight: toNumberOrNull(form.ThemeInputHeight) ?? undefined,
  buttonText: form.SubmitButtonText || "Submit",
});

// ---------------------------------------------------------------------------
// Fields
// ---------------------------------------------------------------------------

// Builder-only field props that have no dedicated column - carried in SettingsJson.
const FIELD_SETTINGS_KEYS = [
  "rows",
  // Change #100018 - the markup of an HTML block. In SettingsJson rather than a
  // column, so no delta beyond the FormFieldType row.
  "html",
  // Task 24766 - conditional visibility, carried whole as an object. No new
  // column, so no delta, and anything that does not understand it round-trips
  // it untouched rather than dropping it.
  "condition",
  // Task 24766 - conditional visibility. An object, carried whole: no new
  // column, so no delta, and a builder that does not understand it round-
  // trips it untouched rather than dropping it.
  "condition",
  "level",
  "checkboxText",
  "multiple",
  // Change #100444 - a File field's minimum number of files (with multiple).
  "minFiles",
  "decimals",
  // Task 24849 - per-field "raise a task on this answer" trigger, carried
  // whole in SettingsJson like `condition`: no new column, no delta.
  // Change #100309 - now a LIST of { value, title, ... } rules, so Yes can
  // raise one task and No another. The old single `taskTrigger` is read in as
  // a one-rule list (fieldFromBundle) and not written back.
  "taskTriggers",
  // Task 24850 - per-field "force an upload on this answer" trigger, an object
  // { value, required }. Same SettingsJson carriage - no new field, no delta.
  "uploadTrigger",
  // Change #100308 - per-field "put a Job Tag on the job on this answer", a
  // list of { value, tagIds[] } rules (Change #100309). Same SettingsJson
  // carriage - no new column.
  "tagTriggers",
  // Change #100636 - per-field "create another form on this answer", a list
  // of { value, formId, instructions } rules. Same SettingsJson carriage.
  "formTriggers",
  // Task 24901 - "can we also prefill a field which is say another field plus 7
  // days". An object { fromKey, offsetDays }. Same SettingsJson carriage as the
  // triggers above: no new column, no delta.
  //
  // Deliberately NOT a DefaultValue token like the Task 24899 prefill sources.
  // Those resolve once, server side, at render. This one has to recompute every
  // time the field it reads from changes, which only the live form can see.
  "derived",
  // Task 24902 - "can we allow a field to be read only so it gets prepopulated
  // and non editable". Stored as true or not at all: see the mapping below for
  // why it is never stored as false.
  "readOnly",
];

const fieldFromBundle = (field) => {
  const settings = safeParseJson(field.SettingsJson, {}) || {};
  const fieldKey = field.FieldKey || nextLocalId("field");
  return {
    // id doubles as the builder DnD key; keep it equal to FieldKey so E-Sign
    // signer/condition field-key references stay stable across a round-trip.
    id: fieldKey,
    Id: field.Id ?? null,
    key: fieldKey,
    type: field.FieldType,
    label: field.Label || "",
    placeholder: field.Placeholder || "",
    helpText: field.HelpText || "",
    value: field.DefaultValue ?? "",
    width: field.Width || "full",
    required: Number(field.FlagRequired) === 1,
    min: field.MinValue ?? settings.min ?? "",
    max: field.MaxValue ?? settings.max ?? "",
    // Task 24701 - repeating groups. Only the repeat_start row carries the
    // settings; repeat_end carries the key alone, which is what pairs them.
    repeatGroupKey: field.RepeatGroupKey || "",
    repeatAddLabel: field.RepeatAddLabel || "",
    repeatMinRows:
      field.RepeatMinRows === null || field.RepeatMinRows === undefined
        ? 1
        : Number(field.RepeatMinRows),
    repeatMaxRows:
      field.RepeatMaxRows === null || field.RepeatMaxRows === undefined
        ? ""
        : Number(field.RepeatMaxRows),
    // Task 24766 - null rather than undefined so "no condition" is an
    // explicit state the inspector can render against.
    condition: settings.condition || null,
    // Task 24849 - null (not undefined) so "no trigger" is an explicit state.
    // Change #100309 - a list; a field saved before then has one rule.
    taskTriggers:
      Array.isArray(settings.taskTriggers) && settings.taskTriggers.length
        ? settings.taskTriggers
        : settings.taskTrigger
        ? [settings.taskTrigger]
        : null,
    // Task 24850 - "force an upload on this answer".
    uploadTrigger: settings.uploadTrigger || null,
    // Change #100308 - "set a tag on the job when answered X".
    tagTriggers:
      Array.isArray(settings.tagTriggers) && settings.tagTriggers.length
        ? settings.tagTriggers
        : null,
    // Change #100636 - "create another form on this answer".
    formTriggers:
      Array.isArray(settings.formTriggers) && settings.formTriggers.length
        ? settings.formTriggers
        : null,
    // Task 24901 - date calculated from another field on this form.
    derived: settings.derived || null,
    // Task 24902 - null rather than false when it is off. fieldToBundle keeps
    // any value that is not undefined/""/null, so a literal false would be
    // written into every field's SettingsJson as a setting nobody set.
    readOnly: settings.readOnly === true ? true : null,
    rows: settings.rows,
    html: settings.html,
    // Change #100635 - the job's equipment log, attached by form/render when
    // it was opened against a job. Never read from or written to SettingsJson,
    // so the builder can't save one job's equipment into the form.
    equipmentLogRows: Array.isArray(field.EquipmentLogRows) ? field.EquipmentLogRows : null,
    equipmentLogJobResolved: Number(field.EquipmentLogJobResolved) === 1,
    level: settings.level,
    checkboxText: settings.checkboxText,
    multiple: settings.multiple,
    minFiles: settings.minFiles,
    decimals: settings.decimals,
    options: (field.formfieldoption_list || []).map((opt) => ({
      id: `opt_${opt.Id ?? nextLocalId("opt")}`,
      Id: opt.Id ?? null,
      label: opt.Label ?? "",
      value: opt.Value ?? "",
    })),
  };
};

// Task 24609 - rows that /form/aibuild returns are already shaped like the
// ones /form/data returns, so they go through the same mapper. They carry no
// Id, which is exactly right: they are new fields until the form is saved.
export const fieldsFromAiBundle = (rows = []) =>
  (rows || []).map((row) => fieldFromBundle(row));

// Task 24698 - each page of a PDF is now read by its own call, and the backend
// makes field keys unique WITHIN a call, not across them. Six calls are six
// namespaces: an "Other ref." on page 1 and another on page 6 both arrived as
// `other_ref`. Key doubles as the builder's DnD id, the React list key and the
// E-Sign signer/condition reference, so a duplicate is not cosmetic - it makes
// two fields indistinguishable. Uniqueness has to be settled where the pages
// are merged, which is here. Seeding it with the keys already on the form also
// covers uploading a second document into a form that has fields already.
export const uniqueFieldKeys = (fields = [], takenKeys = []) => {
  const used = new Set((takenKeys || []).filter(Boolean));
  // Change #100304 - keys renamed in this batch, so a condition inside the
  // batch (an AI-built section watching a question on the same page) follows
  // the question to its new key instead of pointing at a field on the form
  // that merely had the same key first.
  const renamed = {};
  const unique = (fields || []).map((field) => {
    const base = field.key || field.id || "field";
    let candidate = base;
    let suffix = 2;
    // Same _2/_3 shape the backend uses, so a key looks the same however it
    // was disambiguated.
    while (used.has(candidate)) {
      candidate = `${base}_${suffix}`;
      suffix += 1;
    }
    used.add(candidate);
    if (field.key && field.key !== candidate) renamed[field.key] = candidate;
    // Compared against the field's own key/id rather than against `base`, so
    // a field that arrived with neither is given one instead of staying
    // keyless and saving a null FieldKey.
    return field.key === candidate && field.id === candidate
      ? field
      : { ...field, key: candidate, id: candidate };
  });

  return unique.map((field) =>
    field.condition?.fieldKey && renamed[field.condition.fieldKey]
      ? {
          ...field,
          condition: {
            ...field.condition,
            fieldKey: renamed[field.condition.fieldKey],
          },
        }
      : field
  );
};

const fieldToBundle = (field, index) => {
  const settings = {};
  FIELD_SETTINGS_KEYS.forEach((k) => {
    // null is excluded as well as undefined and "" - Task 24766 stores
    // "no condition" as null, and writing `{"condition":null}` into every
    // field's SettingsJson would be noise that reads like a setting.
    if (field[k] !== undefined && field[k] !== "" && field[k] !== null) {
      settings[k] = field[k];
    }
  });
  // Change #100309 - the first task rule is also written as the old single
  // taskTrigger, so a backend that predates the list still raises it. The new
  // backend reads taskTriggers and ignores this when the list is present.
  if (Array.isArray(settings.taskTriggers) && settings.taskTriggers.length) {
    settings.taskTrigger = settings.taskTriggers[0];
  }

  // Number and Decimal both carry a numeric Min/Max range.
  const isNumeric = field.type === "number" || field.type === "decimal";

  return {
    Id: field.Id ?? null,
    FieldType: field.type,
    FieldKey: field.key || field.id,
    Label: field.label ?? "",
    Placeholder: field.placeholder ?? null,
    HelpText: field.helpText ?? null,
    DefaultValue: field.value ?? null,
    Width: field.width || "full",
    SortOrder: (index + 1) * 10,
    FlagRequired: field.required ? 1 : 0,
    MinValue: isNumeric ? toNumberOrNull(field.min) : null,
    MaxValue: isNumeric ? toNumberOrNull(field.max) : null,
    RepeatGroupKey: field.repeatGroupKey || null,
    RepeatAddLabel: field.repeatAddLabel || null,
    // Sent as a real number because the column is NOT NULL DEFAULT 1 - a blank
    // string would be written as 0 and silently make the group optional.
    RepeatMinRows:
      toNumberOrNull(field.repeatMinRows) === null
        ? 1
        : toNumberOrNull(field.repeatMinRows),
    RepeatMaxRows: toNumberOrNull(field.repeatMaxRows),
    SettingsJson: Object.keys(settings).length ? JSON.stringify(settings) : null,
    FlagActive: 1,
    formfieldoption_list: (field.options || []).map((opt, i) => ({
      Id: opt.Id ?? null,
      Label: opt.label ?? "",
      Value: opt.value ?? "",
      SortOrder: (i + 1) * 10,
      FlagActive: 1,
    })),
  };
};

// ---------------------------------------------------------------------------
// Submit behaviour (mapped to the default confirmation row)
// ---------------------------------------------------------------------------

const submitFromBundle = (bundle, storedSubmit) => {
  const confirmations = bundle.formconfirmation_list || [];
  const defaultConfirmation =
    confirmations.find((c) => Number(c.FlagDefault) === 1) ||
    confirmations[0] ||
    null;

  return {
    ...(initialFormBuilderData.submit || {}),
    ...(storedSubmit || {}),
    // Task 24709 - these come from real columns, not from the ThemeJson blob
    // the rest of the submit object lives in, because the SERVER has to read them
    // submission arrives and it cannot act on a JSON column. The column wins
    // over the stored copy for the same reason: it is what actually drives the
    // behaviour.
    createTask: Number(bundle.FlagCreateTask) === 1,
    taskOrganisationDepartmentId: bundle.TaskOrganisationDepartment_Id ?? "",
    taskStaffId: bundle.TaskStaff_Id ?? "",
    successMessage:
      defaultConfirmation?.Message ??
      storedSubmit?.successMessage ??
      initialFormBuilderData.submit?.successMessage ??
      "",
    redirectUrl:
      defaultConfirmation?.RedirectUrl ?? storedSubmit?.redirectUrl ?? "",
    _confirmationId: defaultConfirmation?.Id ?? null,
  };
};

const confirmationListFromSubmit = (submit = {}) => {
  const hasRedirect = !!(submit.redirectUrl && submit.redirectUrl.trim());
  return [
    {
      Id: submit._confirmationId ?? null,
      Name: "Default confirmation",
      Type: hasRedirect ? "redirect" : "message",
      Message: submit.successMessage ?? "",
      RedirectUrl: hasRedirect ? submit.redirectUrl.trim() : null,
      FlagPassFieldData: 0,
      FlagDefault: 1,
      SortOrder: 10,
      FlagActive: 1,
    },
  ];
};

// ---------------------------------------------------------------------------
// E-Sign block <-> ESign* columns
// ---------------------------------------------------------------------------

const esignFromColumns = (form = {}) => ({
  ...(initialFormBuilderData.esign || {}),
  enabled: Number(form.FlagCreateESign) === 1,
  signerSource: form.ESignSignerSource || "field",
  signerNameFieldKey: form.ESignSignerNameFieldKey || "",
  signerEmailFieldKey: form.ESignSignerEmailFieldKey || "",
  signerMobileFieldKey: form.ESignSignerMobileFieldKey || "",
  approveMode: form.ESignApproveMode || "portal",
  reminderScheduleId: form.ESignReminderSchedule_Id ?? "",
  conditionFieldKey: form.ESignConditionFieldKey || "",
  conditionOperator: form.ESignConditionOperator || "any",
  conditionValue: form.ESignConditionValue || "",
  // Fall back to the generic default so the builder always shows editable text.
  confirmationMessage:
    form.ESignConfirmationMessage || DEFAULT_ESIGN_CONFIRMATION,
});

const esignColumns = (esign = {}) => ({
  FlagCreateESign: esign.enabled ? 1 : 0,
  ESignSignerSource: esign.signerSource || "field",
  ESignSignerNameFieldKey: esign.signerNameFieldKey || null,
  ESignSignerEmailFieldKey: esign.signerEmailFieldKey || null,
  ESignSignerMobileFieldKey: esign.signerMobileFieldKey || null,
  ESignApproveMode: esign.approveMode || "portal",
  ESignReminderSchedule_Id: toNumberOrNull(esign.reminderScheduleId),
  ESignConditionFieldKey: esign.conditionFieldKey || null,
  ESignConditionOperator: esign.conditionOperator || "any",
  ESignConditionValue: esign.conditionValue || null,
  ESignConfirmationMessage: esign.confirmationMessage || null,
});

// A genuinely blank form for the "New Form" flow - keeps the default branding /
// submit / esign defaults but with no demo name, header text or fields, so the
// builder opens as a clean slate rather than the sample "Lead Capture Form".
export const makeBlankFormData = () => ({
  ...initialFormBuilderData,
  id: "form_new",
  Id: null,
  Uuid: null,
  name: "",
  status: "draft",
  publicToken: "",
  branding: {
    ...(initialFormBuilderData.branding || {}),
    title: "",
    description: "",
    logoUrl: "",
  },
  submit: { ...(initialFormBuilderData.submit || {}) },
  esign: { ...(initialFormBuilderData.esign || {}) },
  fields: [],
});

// ---------------------------------------------------------------------------
// Public API: normalize (bundle -> formData) and serialize (formData -> save)
// ---------------------------------------------------------------------------

// Backend bundle -> builder formData
export const normalizeFormFromBundle = (bundle) => {
  if (!bundle) return { ...initialFormBuilderData };

  const themeBlob = safeParseJson(bundle.ThemeJson, null) || {};
  const branding = themeBlob.branding || brandingFromColumns(bundle);
  const submit = submitFromBundle(bundle, themeBlob.submit);

  return {
    id: `form_${bundle.Id}`,
    Id: bundle.Id ?? null,
    Uuid: bundle.Uuid ?? null,
    name: bundle.Name || "",
    status: bundle.Status || "draft",
    visibility: bundle.Visibility || "external",
    publicToken: bundle.Slug || bundle.Uuid || "",
    flagStoreSubmissions: Number(bundle.FlagStoreSubmissions ?? 1) === 1,
    branding: { ...(initialFormBuilderData.branding || {}), ...branding },
    submit,
    esign: esignFromColumns(bundle),
    fields: (bundle.formfield_list || []).map(fieldFromBundle),
  };
};

// Builder formData -> form/save payload (single object with nested lists).
// Save preserves status; publishing is a separate endpoint.
export const buildSavePayload = (formData) => {
  const branding = formData.branding || {};
  const submit = formData.submit || {};
  const esign = formData.esign || {};

  const payload = {
    Name: formData.name || "Untitled Form",
    Slug: formData.publicToken || null,
    Visibility: formData.visibility || "external",
    BuildMode: "visual",
    FlagStoreSubmissions: formData.flagStoreSubmissions === false ? 0 : 1,
    // Task 24709 - written as columns as well as into ThemeJson, so the submit
    // handler can read them.
    FlagCreateTask: submit.createTask ? 1 : 0,
    TaskOrganisationDepartment_Id: toNumberOrNull(
      submit.taskOrganisationDepartmentId
    ),
    TaskStaff_Id: toNumberOrNull(submit.taskStaffId),
    ThemeJson: JSON.stringify({ branding, submit, builderVersion: 2 }),
    ...themeColumnsFromBranding(branding),
    ...esignColumns(esign),
    formfield_list: (formData.fields || []).map(fieldToBundle),
    formconfirmation_list: confirmationListFromSubmit(submit),
  };

  // Update vs create: only include Id/Uuid when we already have them.
  if (formData.Id) payload.Id = formData.Id;
  if (formData.Uuid) payload.Uuid = formData.Uuid;

  return payload;
};

// ---------------------------------------------------------------------------
// Status badges (list page)
// ---------------------------------------------------------------------------

/**
 * Task 24888 - the badge for a form row.
 *
 * Prefers the company's OWN status name and colours when the form is on one,
 * and falls back to formStatusMeta's built-in words when it is not. The
 * fallback is not a nicety: a company that has not added any statuses yet has
 * every form on Status = draft/published and nothing else, and those forms must
 * keep reading the way they always did.
 */
export const formStatusBadge = (form) => {
  const name = form?.FormStatus_Name;
  if (!name) {
    return { ...formStatusMeta(form?.Status), style: undefined };
  }
  return {
    label: name,
    // Colours come as inline style, because they are data - a class cannot
    // carry a colour somebody picked this morning.
    badge: "",
    style: {
      background: form?.FormStatus_CssColourBackground || "#E2E8F0",
      color: form?.FormStatus_CssColourText || "#334155",
    },
  };
};

export const formStatusMeta = (status) => {
  switch (String(status || "").toLowerCase()) {
    case "published":
      return { label: "Published", badge: "bg-success-subtle text-success" };
    case "unpublished":
      return {
        label: "Unpublished",
        badge: "bg-warning-subtle text-warning",
      };
    case "draft":
    default:
      return { label: "Draft", badge: "bg-secondary-subtle text-secondary" };
  }
};

// Change #100490 - "when we have two end of sections together it's hard to
// know which is which". An end marker carries no label of its own; it is
// paired to its start on repeatGroupKey, so it borrows the start's label.
export const endMarkerTitle = (field, fields = []) => {
  const isSection = field?.type === FORM_FIELD_TYPES.SECTION_END;
  const base = isSection ? "End of section" : "End of repeating section";
  const start = field?.repeatGroupKey
    ? fields.find(
        (f) =>
          f.repeatGroupKey === field.repeatGroupKey &&
          f.type ===
            (isSection
              ? FORM_FIELD_TYPES.SECTION_START
              : FORM_FIELD_TYPES.REPEAT_START)
      )
    : null;
  return start?.label ? `${base}: ${start.label}` : base;
};
